import { Controller } from "stimulus"
import { showLoader, hideLoader } from '../utils/loader'

export default class extends Controller {
  static values = {
    usersUrl: String
  }
  static targets = ["usersTableWrapper", "usersTableBody", "pagination", "selectedUserId", "submitButton", "selectedUserInfo"]

  connect() {
    this.timeout = null
  }

  updateOrganization(event) {
    const orgId = event.target.value
    if (!orgId) {
      this.hideTable()
      return
    }

    this.showTable()
    this.clearSearchInputs()
    this.loadUsers(orgId)
  }

  showTable() {
    if (this.hasUsersTableWrapperTarget) {
      this.usersTableWrapperTarget.classList.remove('d-none')
    }
  }

  hideTable() {
    if (this.hasUsersTableWrapperTarget) {
      this.usersTableWrapperTarget.classList.add('d-none')
    }
    this.clearSelection()
  }

  clearSearchInputs() {
    this.element.querySelectorAll('.js-search-input').forEach(input => {
      input.value = ''
    })
    const sortInput = this.element.querySelector('.js-sort-input')
    if (sortInput) sortInput.value = 'first_name asc'
    const pageInput = this.element.querySelector('.js-page-input')
    if (pageInput) pageInput.value = '1'
    this.updateSortIndicators('first_name', 'asc')
  }

  loadUsers(orgId, searchParams = '') {
    let url = this.usersUrlValue.replace('ORG_ID', orgId)
    if (searchParams) {
      url += '&' + searchParams
    }
    showLoader()

    fetch(url, {
      method: 'GET',
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      },
      credentials: 'same-origin'
    })
    .then(response => {
      if (!response.ok) throw new Error('Request failed')
      return response.json()
    })
    .then(data => {
      if (this.hasUsersTableBodyTarget) {
        this.usersTableBodyTarget.innerHTML = data.rows
      }
      if (this.hasPaginationTarget) {
        this.paginationTarget.innerHTML = data.pagination
        this.setupPaginationListeners()
      }
      this.clearSelection()
    })
    .catch(error => {
      console.error('Error loading users:', error)
      if (this.hasUsersTableBodyTarget) {
        this.usersTableBodyTarget.innerHTML = '<tr><td colspan="3" class="text-center text-danger p-3">Error loading users.</td></tr>'
      }
    })
    .finally(() => {
      hideLoader()
    })
  }

  setupPaginationListeners() {
    if (this.hasPaginationTarget) {
      this.paginationTarget.querySelectorAll('a').forEach(link => {
        link.addEventListener('click', (e) => this.handlePagination(e))
      })
    }
  }

  handleSearchInput() {
    clearTimeout(this.timeout)
    this.timeout = setTimeout(() => {
      this.resetPage()
      this.submitSearch()
    }, 300)
  }

  handleSort(event) {
    event.preventDefault()
    const sortField = event.currentTarget.dataset.sortField

    const sortInput = this.element.querySelector('.js-sort-input')
    if (sortInput) {
      const currentSort = sortInput.value || ''
      const parts = currentSort.split(' ')
      const field = parts[0] || ''
      const direction = parts[1] || ''

      let newDirection = 'asc'
      if (field === sortField && direction === 'asc') {
        newDirection = 'desc'
      }
      sortInput.value = `${sortField} ${newDirection}`
      this.updateSortIndicators(sortField, newDirection)
    }

    this.resetPage()
    this.submitSearch()
  }

  updateSortIndicators(activeField, direction) {
    this.element.querySelectorAll('.js-sort-indicator').forEach(indicator => {
      const field = indicator.dataset.field
      if (field === activeField) {
        indicator.textContent = direction === 'asc' ? ' ▲' : ' ▼'
      } else {
        indicator.textContent = ''
      }
    })
  }

  resetPage() {
    const pageInput = this.element.querySelector('.js-page-input')
    if (pageInput) pageInput.value = '1'
  }

  handlePagination(event) {
    event.preventDefault()
    const link = event.currentTarget
    if (link.classList.contains('disabled')) return

    const linkUrl = new URL(link.href, window.location.origin)
    const page = linkUrl.searchParams.get('page') || 1

    const pageInput = this.element.querySelector('.js-page-input')
    if (pageInput) pageInput.value = page

    this.submitSearch()
  }

  submitSearch() {
    const searchParams = new URLSearchParams()

    // Get sort value
    const sortInput = this.element.querySelector('.js-sort-input')
    if (sortInput && sortInput.value) {
      searchParams.set('q[s]', sortInput.value)
    }

    // Get page value
    const pageInput = this.element.querySelector('.js-page-input')
    if (pageInput && pageInput.value) {
      searchParams.set('page', pageInput.value)
    }

    // Get search inputs
    this.element.querySelectorAll('.js-search-input').forEach(input => {
      if (input.value) {
        searchParams.set(input.name, input.value)
      }
    })

    const orgSelect = this.element.querySelector('[data-action*="updateOrganization"]')
    if (orgSelect && orgSelect.value) {
      this.loadUsers(orgSelect.value, searchParams.toString())
    }
  }

  selectUser(event) {
    const row = event.currentTarget
    const userId = row.dataset.userId
    const userName = row.dataset.userName
    const userEmail = row.dataset.userEmail

    this.selectedUserIdTarget.value = userId
    if (this.hasUsersTableBodyTarget) {
      this.usersTableBodyTarget.querySelectorAll('.js-user-row').forEach(r => r.classList.remove('table-primary'))
    }
    row.classList.add('table-primary')

    if (this.hasUsersTableBodyTarget) {
      this.usersTableBodyTarget.querySelectorAll('input[type="radio"]').forEach(radio => radio.checked = false)
    }
    const radio = row.querySelector('input[type="radio"]')
    if (radio) radio.checked = true

    if (this.hasSelectedUserInfoTarget) {
      this.selectedUserInfoTarget.textContent = ''
      const strong = document.createElement('strong')
      strong.textContent = 'Selected: '
      this.selectedUserInfoTarget.appendChild(strong)
      this.selectedUserInfoTarget.appendChild(document.createTextNode(`${userName} (${userEmail})`))
      this.selectedUserInfoTarget.classList.remove('d-none')
    }

    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = false
    }
  }

  clearSelection() {
    if (this.hasSelectedUserIdTarget) {
      this.selectedUserIdTarget.value = ''
    }
    if (this.hasSelectedUserInfoTarget) {
      this.selectedUserInfoTarget.innerHTML = ''
      this.selectedUserInfoTarget.classList.add('d-none')
    }
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = true
    }
  }

}
